/**
 * Nesting the release list: a flat, version-ordered list of `releases` rows
 * into RELEASES (10.2) and the VERSIONS shipped under them (10.2.0.27).
 *
 * The same shape the command center's `crm/pages/releases/release-families.ts`
 * builds, so the two Releases pages read alike — with ONE difference: a
 * reseller sees only what has SHIPPED. An unreleased version is XOP's
 * roadmap, not something a partner can install, and a release with nothing
 * shipped under it is a folder with nothing in it.
 *
 * Pure, so the nesting can be proven without mounting a page.
 */
import { compareVersionsDesc, type ReleaseRow } from "./api.js";

/** Newest first, by version NUMBER (10.1.0 after 9.3.0, not before). */
export function sortReleasesNewestFirst(rows: readonly ReleaseRow[]): ReleaseRow[] {
  return [...rows].sort((a, b) => compareVersionsDesc(a.version, b.version));
}

/** A release and the versions shipped under it, newest first. */
export interface ReleaseFamily {
  /** The release — or, for a version whose release is not in the list, the
   *  version itself, so nothing is ever dropped for want of a parent. */
  line: ReleaseRow;
  builds: ReleaseRow[];
  /** False when `line` is really an orphan version standing in for one. */
  hasLine: boolean;
}

/** The versions inside a release folder. For an orphan — a version whose
 *  release is not in the list — that is the version itself. */
export function versionsOf(family: ReleaseFamily): ReleaseRow[] {
  return family.hasLine ? family.builds : [family.line];
}

/** The folder a path names, by the version printed on it. */
export function findFamily(
  families: readonly ReleaseFamily[],
  version: string | null | undefined,
): ReleaseFamily | undefined {
  if (!version) return undefined;
  return families.find((f) => f.line.version === version);
}

/**
 * Nest the flat list into families of SHIPPED releases.
 *
 * Families are ordered by the newest version in each, not by the release's
 * own number: an empty 12.0 must not outrank a live 11.2 that is still
 * shipping. A version's release is kept even if the release row itself is
 * not marked released — the versions under it are what shipped.
 */
export function groupIntoFamilies(rows: readonly ReleaseRow[]): ReleaseFamily[] {
  const byId = new Map(rows.map((r) => [r.id, r]));
  // `== null` on purpose: during a rolling deploy the SPA can be newer than
  // portal-api, which then omits the field. No parent — null or absent — is
  // a release.
  const lines = rows.filter((r) => r.parent_release_id == null);
  const buildsByLine = new Map<number, ReleaseRow[]>();
  const orphans: ReleaseRow[] = [];

  for (const row of rows) {
    const parentId = row.parent_release_id;
    if (parentId == null) continue;
    if (!row.released) continue;
    if (!byId.has(parentId)) {
      orphans.push(row);
      continue;
    }
    const list = buildsByLine.get(parentId);
    if (list) list.push(row);
    else buildsByLine.set(parentId, [row]);
  }

  const families: ReleaseFamily[] = [];
  for (const line of lines) {
    const builds = sortReleasesNewestFirst(buildsByLine.get(line.id) ?? []);
    // Shipped versions, or a release that shipped as a whole with none
    // recorded under it. A planned release with nothing shipped is roadmap.
    if (builds.length === 0 && !line.released) continue;
    if (builds.length === 0 && rows.some((r) => r.parent_release_id === line.id)) continue;
    families.push({ line, builds, hasLine: true });
  }
  for (const orphan of orphans) {
    families.push({ line: orphan, builds: [], hasLine: false });
  }

  const newestOf = (f: ReleaseFamily): string => f.builds[0]?.version ?? f.line.version;
  return families.sort((a, b) => compareVersionsDesc(newestOf(a), newestOf(b)));
}

/** The newest shipped VERSION across every family — a release is a folder
 *  name, not something anyone runs. */
export function latestVersion(families: readonly ReleaseFamily[]): ReleaseRow | undefined {
  const versions = families.flatMap(versionsOf);
  return sortReleasesNewestFirst(versions)[0];
}
